import type { ReploAnnouncement } from "schemas/generated/announcement";

import * as React from "react";
import { useState } from "react";

import { Collapsible } from "@replohq/lattice/components/collapsible/Collapsible";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replohq/lattice/components/menu/Menu";
import { Popover } from "@replohq/lattice/components/popover/Popover";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@replohq/lattice/components/sidebar/Sidebar";
import { cn } from "@replohq/lattice/utils/cn";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import AnnouncementsMenuContent from "@v2/features/announcements/AnnouncementsMenuContent";
import { useListAnnouncements } from "@v2/features/announcements/hooks/useListAnnouncements";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  ArrowUpRight,
  ChevronRight,
  FileText,
  GraduationCap,
  LibraryBig,
  Megaphone,
  MessageSquareShare,
  ThumbsUp,
  UserCheck,
} from "lucide-react";

type ResourceLink = {
  title: string;
  href: string;
  icon: React.ReactNode;
};

function getResourceLinks(hostingType: string): ResourceLink[] {
  if (hostingType === "replo") {
    return [
      {
        title: "Help Docs",
        href: "https://docs.replo.app",
        icon: <FileText size={16} />,
      },
      {
        title: "Join the Discord",
        href: "https://discord.gg/E7K6VADQJM",
        icon: <MessageSquareShare size={16} />,
      },
      {
        title: "Feedback",
        href: "https://feedback.replo.app/?utm_source=dashboard",
        icon: <ThumbsUp size={16} />,
      },
    ];
  }

  return [
    {
      title: "Reploversity",
      href: "https://www.replo.app/learn",
      icon: <GraduationCap size={16} />,
    },
    {
      title: "Help Docs",
      href: "https://support.replo.app/",
      icon: <FileText size={16} />,
    },
    {
      title: "Replo Experts",
      href: "https://contra.com/replo",
      icon: <UserCheck size={16} />,
    },
    {
      title: "Join the Discord",
      href: "https://discord.gg/E7K6VADQJM",
      icon: <MessageSquareShare size={16} />,
    },
    {
      title: "Feedback",
      href: "https://feedback.replo.app/?utm_source=dashboard",
      icon: <ThumbsUp size={16} />,
    },
  ];
}

const WhatsNewSubButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const logEvent = useLogAnalytics();
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<ReploAnnouncement | null>(null);
  const { announcements, isLoading } = useListAnnouncements({ limit: 4 });

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      title={selectedAnnouncement ? undefined : "What's New"}
      side="right"
      align="end"
      sideOffset={16}
      UNSAFE_className={cn(
        "min-h-[375px] max-h-[375px] w-[300px] overflow-hidden",
        selectedAnnouncement ? "p-0" : "p-3",
      )}
      showCloseButton={!Boolean(selectedAnnouncement)}
      trigger={
        <SidebarMenuSubButton
          onClick={() => {
            setIsOpen(true);
            logEvent("announcements.menu.opened", {});
          }}
        >
          <Megaphone />
          <span>What's New</span>
        </SidebarMenuSubButton>
      }
    >
      <AnnouncementsMenuContent
        announcements={announcements}
        isLoading={isLoading}
        handleClose={() => setIsOpen(false)}
        selectedAnnouncement={selectedAnnouncement}
        setSelectedAnnouncement={setSelectedAnnouncement}
      />
    </Popover>
  );
};

const LearnMoreCollapsible = ({
  resourceLinks,
}: {
  resourceLinks: ResourceLink[];
}) => {
  return (
    <SidebarMenuItem>
      <Collapsible
        trigger={
          <SidebarMenuButton tooltip="Learn More">
            <LibraryBig />
            <span>Learn More</span>
            <ChevronRight className="ml-auto text-placeholder transition-transform [[data-panel-open]_&]:rotate-90" />
          </SidebarMenuButton>
        }
      >
        <SidebarMenuSub>
          <SidebarMenuSubItem>
            <WhatsNewSubButton />
            {resourceLinks.map((link) => (
              <SidebarMenuSubButton
                key={link.title}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.icon}
                <span>{link.title}</span>
                <ArrowUpRight className="ml-auto shrink-0 text-placeholder!" />
              </SidebarMenuSubButton>
            ))}
          </SidebarMenuSubItem>
        </SidebarMenuSub>
      </Collapsible>
    </SidebarMenuItem>
  );
};

const LearnMoreMenu = ({
  resourceLinks,
}: {
  resourceLinks: ResourceLink[];
}) => {
  const logEvent = useLogAnalytics();

  return (
    <SidebarMenuItem>
      <Menu>
        <MenuTrigger className="w-full">
          <SidebarMenuButton tooltip="Learn More">
            <LibraryBig />
          </SidebarMenuButton>
        </MenuTrigger>
        <MenuContent side="right" sideOffset={16} align="end">
          <MenuItem
            label="What's New"
            startEnhancer={<Megaphone size={14} />}
            endEnhancer={
              <ArrowUpRight size={14} className="text-placeholder" />
            }
            onClick={() => {
              logEvent("announcements.menu.opened", {});
              window.open(
                "https://www.replo.app/changelog",
                "_blank",
                "noopener,noreferrer",
              );
            }}
          />
          {resourceLinks.map((link) => (
            <MenuItem
              key={link.title}
              label={link.title}
              startEnhancer={link.icon}
              endEnhancer={<ArrowUpRight className="text-placeholder" />}
              onClick={() => {
                window.open(link.href, "_blank", "noopener,noreferrer");
              }}
            />
          ))}
        </MenuContent>
      </Menu>
    </SidebarMenuItem>
  );
};

const LearnMoreCollapsibleNavItem = () => {
  const { project } = useCurrentProjectContext();
  const resourceLinks = getResourceLinks(project?.hostingType ?? "replo");
  const { open } = useSidebar();

  if (!open) {
    return <LearnMoreMenu resourceLinks={resourceLinks} />;
  }

  return <LearnMoreCollapsible resourceLinks={resourceLinks} />;
};

export default LearnMoreCollapsibleNavItem;
